import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ChecklistStatusIcon } from "@/components/ChecklistStatusIcon";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Building2, Camera, X, Loader2, CheckCircle2, Image as ImageIcon, PenLine, Search, Ban, Sparkles, Check, Plus as PlusIcon, Lock, FileDown, AlertTriangle, Pin } from "lucide-react";

const VAL_BUTTONS = [
  { v: "perfetto", label: "Perfetto", color: "bg-emerald-500 text-white hover:bg-emerald-600 border-emerald-500" },
  { v: "buono", label: "Buono", color: "bg-lime-400 text-lime-950 hover:bg-lime-500 border-lime-400" },
  { v: "sufficiente", label: "Sufficiente", color: "bg-amber-400 text-amber-950 hover:bg-amber-500 border-amber-400" },
  { v: "insufficiente", label: "Insufficiente", color: "bg-rose-500 text-white hover:bg-rose-600 border-rose-500" },
  // v101 — N/A: valutazione non applicabile (es. hotel senza bar per task "Controllo Bar")
  { v: "na", label: "N/A", color: "bg-slate-400 text-white hover:bg-slate-500 border-slate-400" },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ControlloDettaglio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const fileRef = useRef(null);
  // Return path preservato dall'elenco Controlli (mantiene filtri via URL params)
  const returnTo = location.state?.returnTo || "/controlli";
  const goBackToList = () => navigate(returnTo);

  const [check, setCheck] = useState(null);
  const [template, setTemplate] = useState(null);
  const [hotel, setHotel] = useState(null);
  const [results, setResults] = useState({});  // item_id -> {valore, note}
  const [commenti, setCommenti] = useState("");
  const [valutazione, setValutazione] = useState(null);
  const [fotos, setFotos] = useState([]);  // {id, url}
  const [actualCount, setActualCount] = useState("");
  const [rooms, setRooms] = useState([]);
  const [roomsChecked, setRoomsChecked] = useState(new Set());
  const [roomsSuggested, setRoomsSuggested] = useState(new Set());
  const [roomsSkipped, setRoomsSkipped] = useState([]);
  const [perRoomRecords, setPerRoomRecords] = useState([]);
  const [camereData, setCamereData] = useState(null); // { rooms, counters, ... }
  const [selectFilter, setSelectFilter] = useState("all"); // all | selected | to_compile | done | ieri | settimana
  const [belowThresholdOpen, setBelowThresholdOpen] = useState(false);
  const [inSospesoConfirmOpen, setInSospesoConfirmOpen] = useState(false);
  const [deselectMenu, setDeselectMenu] = useState(null); // room object
  const [skipTarget, setSkipTarget] = useState(null);
  const [skipMotivo, setSkipMotivo] = useState("DND / Non disturbare");
  const [skipNote, setSkipNote] = useState("");
  const [roomSearch, setRoomSearch] = useState("");
  // v102 — Pin recap sticky durante lo scroll (persistenza localStorage)
  const [recapPinned, setRecapPinned] = useState(() => {
    try { return localStorage.getItem("controlli_recap_pinned") === "1"; } catch { return false; }
  });
  const toggleRecapPin = () => {
    setRecapPinned((v) => {
      const next = !v;
      try { localStorage.setItem("controlli_recap_pinned", next ? "1" : "0"); } catch {}
      return next;
    });
  };
  // v101 — Modal N/A: richiede nota obbligatoria quando valutazione = "na"
  const [naModalOpen, setNaModalOpen] = useState(false);
  const [naNote, setNaNote] = useState("");
  const [roomPianoFilter, setRoomPianoFilter] = useState("__all__");
  const [uploading, setUploading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [forceOpen, setForceOpen] = useState(false);
  const [forceReason, setForceReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const downloadRoomsPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get(`/checks/${id}/rooms-detail/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      // Prova a leggere filename dall'header (potrebbe non essere esposto in CORS)
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/.exec(cd);
      const filename = m?.[1] || `controllo-camere-${id}.pdf`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("PDF scaricato");
    } catch (err) {
      toast.error(formatApiError(err) || "Impossibile generare il PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const refreshRooms = async () => {
    try {
      const { data: rd } = await api.get(`/checks/${id}/rooms`);
      setPerRoomRecords(rd.check ? (await api.get(`/checks/${id}`)).data.per_room_records || [] : []);
      setRoomsSuggested(new Set((rd.rooms || []).map((r) => r.room_id).concat((rd.skipped || []).map((s) => s.room_id))));
      setRoomsSkipped(rd.skipped || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data: c } = await api.get(`/checks/${id}`);
        setCheck(c);
        const initR = {};
        (c.checklist_results || []).forEach((r) => {
          initR[r.item_id] = { valore: r.valore, note: r.note || "" };
        });
        setResults(initR);
        setCommenti(c.commenti || "");
        setValutazione(c.valutazione || null);
        // Foto: from foto_ids load minimal info (we have URL)
        setFotos((c.foto_ids || []).map((fid) => ({ id: fid, url: `${BACKEND_URL}/api/uploads/${fid}` })));
        setActualCount(c.actual_count != null ? String(c.actual_count) : "");
        setRoomsChecked(new Set(c.rooms_checked || []));
        setRoomsSuggested(new Set(c.rooms_suggested || []));
        setRoomsSkipped(c.rooms_skipped || []);
        setPerRoomRecords(c.per_room_records || []);
        const [tpl, h] = await Promise.all([
          api.get(`/templates/${c.template_id}`),
          api.get(`/hotels/${c.hotel_id}`),
        ]);
        setTemplate(tpl.data);
        setHotel(h.data);
        if (c.categoria === "camere") {
          try {
            const rs = await api.get(`/hotels/${c.hotel_id}/rooms`, { params: { attiva: true } });
            setRooms(rs.data || []);
          } catch { /* ignore */ }
        }
      } catch (err) {
        toast.error("Controllo non disponibile");
      }
    })();
  }, [id]);

  const isReadOnly = check?.stato === "completato" || user?.role === "read_only";

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8MB"); return; }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error("Solo JPG/PNG/WebP"); return; }
    // v101 — Limite foto per categoria: locali_tecnici max 10, altre nessun limite
    const photoLimit = check?.categoria === "locali_tecnici" ? 10 : null;
    if (photoLimit != null && fotos.length >= photoLimit) {
      toast.error(`Massimo ${photoLimit} foto per questa task (${check.categoria.replace("_", " ")})`);
      return;
    }
    setUploading(true);
    // Compressione client-side prima dell'upload (fallback silenzioso all'originale in caso di errore)
    const { compressPhoto } = await import("@/lib/imageCompression");
    const compressed = await compressPhoto(file);
    const form = new FormData();
    form.append("file", compressed, compressed.name || file.name);
    form.append("daily_check_id", id);
    form.append("hotel_id", check.hotel_id);
    try {
      const { data } = await api.post("/photos/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setFotos((arr) => [...arr, { id: data.id, url: `${BACKEND_URL}${data.url}` }]);
      toast.success("Foto caricata");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeFoto = async (fid) => {
    try {
      await api.delete(`/photos/${fid}`);
      setFotos((arr) => arr.filter((f) => f.id !== fid));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const checklistItems = useMemo(() => template?.checklist_items || [], [template]);
  const isCamere = check?.categoria === "camere";

  // Fetch camereData (new selection API)
  const loadCamere = async () => {
    if (!id) return;
    try {
      const { data } = await api.get(`/checks/${id}/rooms`);
      setCamereData(data);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    if (isCamere) loadCamere();
    // eslint-disable-next-line
  }, [isCamere, id]);

  const toggleSelect = async (room, selected, reason) => {
    if (isReadOnly) return;
    if (!selected && room.compilata) {
      toast.error("Camera già compilata: elimina prima la valutazione dal dettaglio camera");
      return;
    }
    try {
      await api.put(`/checks/${id}/rooms/${room.room_id}/select`, {
        selected, deselect_reason: reason || undefined,
      });
      await loadCamere();
      setDeselectMenu(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // v101 — "Suggerisci 20%": chiama l'endpoint rotation e seleziona le camere consigliate
  const [suggesting, setSuggesting] = useState(false);
  const handleSuggest20 = async () => {
    if (isReadOnly || suggesting) return;
    setSuggesting(true);
    try {
      const { data } = await api.post(`/checks/${id}/suggest-rooms`);
      const roomsSug = data?.rooms || [];
      if (!roomsSug.length) {
        toast.info("Nessuna camera da suggerire (hotel senza camere attive)");
        return;
      }
      // Seleziona in parallelo (ignora quelle già selezionate)
      const currentSelected = new Set((camereData?.rooms || []).filter((r) => r.is_selected).map((r) => r.room_id));
      const toSelect = roomsSug.filter((r) => !currentSelected.has(r.room_id));
      await Promise.all(toSelect.map((r) =>
        api.put(`/checks/${id}/rooms/${r.room_id}/select`, { selected: true }).catch(() => null)
      ));
      await loadCamere();
      toast.success(`Suggerite ${roomsSug.length} camere non controllate di recente — modifica se vuoi`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSuggesting(false);
    }
  };

  // v101 — "Deseleziona tutto": rimuove tutte le camere selezionate non compilate
  const handleDeselectAll = async () => {
    if (isReadOnly) return;
    const toDeselect = (camereData?.rooms || []).filter((r) => r.is_selected && !r.compilata);
    if (!toDeselect.length) {
      toast.info("Nessuna camera da deselezionare");
      return;
    }
    try {
      await Promise.all(toDeselect.map((r) =>
        api.put(`/checks/${id}/rooms/${r.room_id}/select`, { selected: false }).catch(() => null)
      ));
      await loadCamere();
      toast.success(`${toDeselect.length} camer${toDeselect.length === 1 ? "a deselezionata" : "e deselezionate"}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Per-room compilation map
  const recordedIds = useMemo(() => new Set((perRoomRecords || []).filter((r) => r.valutazione).map((r) => r.room_id)), [perRoomRecords]);
  const skippedIds = useMemo(() => new Set((roomsSkipped || []).map((s) => s.room_id)), [roomsSkipped]);
  const targetRoomIds = useMemo(() => Array.from(roomsSuggested).filter((rid) => !skippedIds.has(rid)), [roomsSuggested, skippedIds]);
  const compiledCount = targetRoomIds.filter((rid) => recordedIds.has(rid)).length;
  const missingRoomsCount = targetRoomIds.length - compiledCount;
  const camerePunteggio = useMemo(() => {
    const scores = (perRoomRecords || [])
      .filter((r) => targetRoomIds.includes(r.room_id) && r.valutazione)
      .map((r) => ({ perfetto: 100, buono: 80, sufficiente: 60, insufficiente: 30 }[r.valutazione] || 0));
    if (!scores.length) return null;
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  }, [perRoomRecords, targetRoomIds]);
  const isLegacyCompletato = isCamere && check?.stato === "completato" && (!perRoomRecords || perRoomRecords.length === 0);

  const missing = useMemo(() => {
    if (isCamere) return { items: [], foto: false, requiresFoto: false };
    // v101 — N/A: nessun item obbligatorio richiesto
    if (valutazione === "na") return { items: [], foto: false, requiresFoto: false };
    const missReq = [];
    for (const it of checklistItems) {
      if (it.obbligatorio && (results[it.id]?.valore === null || results[it.id]?.valore === undefined)) {
        missReq.push(it.label);
      }
    }
    return { items: missReq, foto: false, requiresFoto: false };
  }, [checklistItems, results, isCamere, valutazione]);

  const canComplete = !isReadOnly && (isCamere
    ? (camereData && camereData.counters && (camereData.counters.compilate ?? 0) >= (camereData.counters.minimo ?? camereData.counters.minimo_richiesto ?? 0) && (camereData.counters.minimo ?? camereData.counters.minimo_richiesto ?? 0) > 0)
    : (valutazione && missing.items.length === 0));

  const submit = async (force = false, forceReason = "") => {
    if (!force && !canComplete) return;
    setSubmitting(true);
    try {
      const payload = isCamere
        ? {
            checklist_results: [],
            commenti,
            valutazione: null,
            foto_ids: fotos.map((f) => f.id),
            rooms_skipped: roomsSkipped,
            force_complete: !!force,
            force_complete_reason: force ? forceReason : null,
          }
        : {
            checklist_results: checklistItems.map((it) => ({
              item_id: it.id,
              label: it.label,
              valore: results[it.id]?.valore ?? null,
              note: results[it.id]?.note || "",
            })),
            commenti,
            valutazione,
            foto_ids: fotos.map((f) => f.id),
          };
      await api.patch(`/checks/${id}/complete`, payload);
      toast.success(force ? "Controllo chiuso sotto soglia (motivazione registrata)" : "Controllo completato e firmato");
      setConfirmOpen(false);
      setForceOpen(false);
      goBackToList();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const roomNumberById = (rid) => rooms.find((r) => r.id === rid)?.numero || "—";

  const openSkipModal = (room) => {
    if (isReadOnly) return;
    setSkipTarget(room);
    setSkipMotivo("DND / Non disturbare");
    setSkipNote("");
  };

  const submitSkipRoom = async () => {
    if (!skipTarget) return;
    if (skipMotivo === "Altro" && !skipNote.trim()) {
      toast.error("Per il motivo 'Altro' è obbligatorio specificare una nota");
      return;
    }
    try {
      const { data } = await api.post(`/checks/${id}/skip-room`, {
        room_id: skipTarget.id,
        motivo: skipMotivo,
        note: skipNote.trim() || undefined,
      });
      setRoomsSuggested(new Set(data.rooms_suggested || []));
      setRoomsSkipped(data.rooms_skipped || []);
      // Auto-deselect if it was checked
      setRoomsChecked((prev) => {
        const next = new Set(prev);
        next.delete(skipTarget.id);
        return next;
      });
      const newRoom = data.new_suggested_room;
      if (newRoom) {
        toast.success(`Camera ${skipTarget.numero} saltata — sostituita con ${newRoom.numero}`);
      } else {
        toast.success(`Camera ${skipTarget.numero} saltata — nessuna sostituzione disponibile`);
      }
      setSkipTarget(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!check || !template) {
    return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>;
  }

  return (
    <div className="space-y-6 ih-anim-in max-w-4xl" data-testid="controllo-dettaglio">
      <Button variant="ghost" onClick={goBackToList} className="gap-1"><ArrowLeft className="h-4 w-4" /> Indietro</Button>

      <div className="ih-glass rounded-2xl p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">{check.categoria.replaceAll("_", " ")} · {check.frequenza}</div>
        <h1 className="font-display text-3xl tracking-tight font-light mb-2">{check.titolo}</h1>
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5"><Building2 className="h-4 w-4" /> {hotel?.nome}</span>
          <span>Scadenza: {check.scadenza ? new Date(check.scadenza).toLocaleString("it-IT") : "—"}</span>
          {isReadOnly && check.firma && (
            <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="h-4 w-4" /> Firmato</span>
          )}
        </div>
        {isReadOnly && (
          <div
            data-testid="readonly-banner"
            className="mt-4 rounded-xl border border-lavender-300/60 bg-lavender-50/70 dark:bg-lavender-900/20 dark:border-lavender-800/50 px-4 py-2.5 flex items-center gap-2.5 text-sm flex-wrap"
          >
            <Lock className="h-4 w-4 text-lavender-700 dark:text-lavender-300 shrink-0" />
            <span className="flex-1 min-w-[220px]">
              Controllo chiuso
              {check.firma?.data ? ` il ${new Date(check.firma.data).toLocaleString("it-IT", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}
              {check.completer_name ? ` da ${check.completer_name}` : ""}
              {" — sola lettura"}
            </span>
            {isCamere && (
              <Button
                data-testid="export-pdf-btn"
                size="sm"
                variant="outline"
                onClick={downloadRoomsPdf}
                disabled={downloadingPdf}
                className="gap-1.5 border-lavender-400/70 text-lavender-800 dark:text-lavender-200 hover:bg-lavender-100/50 dark:hover:bg-lavender-900/40"
              >
                {downloadingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                Esporta PDF
              </Button>
            )}
          </div>
        )}
      </div>

      {isCamere && camereData && (() => {
        const c = camereData.counters || {};
        const rooms = camereData.rooms || [];
        const targetPct = c.target_pct ?? 20;
        const totale = c.totale_hotel ?? c.totale_camere_hotel ?? 0;
        const minimo = c.minimo ?? c.minimo_richiesto ?? 0;
        const sel = c.selezionate || 0;
        const compilate = c.compilate ?? 0;
        const inSospeso = c.in_sospeso ?? Math.max(0, sel - compilate);
        const mancantiAlMinimo = c.mancanti_al_minimo ?? Math.max(0, minimo - compilate);
        const sup = c.superamento || 0;
        const belowThreshold = compilate < minimo;
        const pct = minimo ? Math.min(100, Math.round((compilate / minimo) * 100)) : 0;
        const barCls = belowThreshold ? "bg-rose-500" : "bg-emerald-500";
        // Aggregati riepilogo (solo readonly): riusa la valutazione persistita
        const compiledRooms = rooms.filter((r) => r.compilata && r.record);
        const conformiCount = compiledRooms.filter((r) => ["perfetto", "buono"].includes(r.record?.valutazione)).length;
        const nonConformiCount = compiledRooms.filter((r) => ["sufficiente", "insufficiente"].includes(r.record?.valutazione)).length;
        const criticheCount = compiledRooms.filter((r) => (r.record?.voci || []).some((v) => v.stato === "non_ok")).length;
        const conformiPct = compiledRooms.length ? Math.round((conformiCount / compiledRooms.length) * 100) : 0;
        const nonConformiPct = compiledRooms.length ? Math.round((nonConformiCount / compiledRooms.length) * 100) : 0;
        const criticitaPct = compiledRooms.length ? Math.round((criticheCount / compiledRooms.length) * 100) : 0;
        const search = roomSearch.trim().toLowerCase();
        const filtered = rooms.filter((r) => {
          if (search && !String(r.numero || "").toLowerCase().includes(search)) return false;
          if (selectFilter === "selected" && !r.is_selected) return false;
          if (selectFilter === "to_compile" && (!r.is_selected || r.compilata)) return false;
          if (selectFilter === "done" && !r.compilata) return false;
          if (selectFilter === "ieri" && r.badge !== "ieri") return false;
          if (selectFilter === "settimana" && r.badge !== "settimana") return false;
          if (selectFilter === "non_conformi" && !(r.compilata && ["sufficiente", "insufficiente"].includes(r.record?.valutazione))) return false;
          if (selectFilter === "critiche" && !(r.compilata && (r.record?.voci || []).some((v) => v.stato === "non_ok"))) return false;
          return true;
        });
        const VOTI = { perfetto: { l: "Perfetto", cls: "bg-emerald-500" }, buono: { l: "Buono", cls: "bg-lavender-500" }, sufficiente: { l: "Sufficiente", cls: "bg-amber-500" }, insufficiente: { l: "Insufficiente", cls: "bg-rose-500" } };
        const FILTERS = isReadOnly && !isLegacyCompletato
          ? [
              { k: "all", l: "Tutte" },
              { k: "non_conformi", l: `Non conformi${nonConformiCount ? ` · ${nonConformiCount}` : ""}` },
              { k: "critiche", l: `Con criticità${criticheCount ? ` · ${criticheCount}` : ""}` },
            ]
          : [
              { k: "all", l: "Tutte" },
              { k: "selected", l: "Selezionate" },
              { k: "to_compile", l: "Da compilare" },
              { k: "done", l: "Compilate" },
              { k: "ieri", l: "Badge ieri" },
              { k: "settimana", l: "Badge settimana" },
            ];
        return (
          <div className="ih-glass rounded-2xl p-6 border-l-4 border-l-lavender-400 space-y-4" data-testid="camere-block">
            {/* Sticky metrics card — v102: pinnable in edit mode */}
            <div
              className={`rounded-2xl p-4 shadow-sm transition-all ${
                !isReadOnly && recapPinned ? "sticky top-[76px] z-30" : ""
              }`}
              style={{ background: "linear-gradient(135deg, rgba(231,215,246,0.9) 0%, rgba(255,255,255,0.95) 100%)", backdropFilter: "blur(8px)" }}
              data-testid="camere-metrics"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="text-[10px] uppercase tracking-[0.18em] text-lavender-800 dark:text-lavender-200 mb-2">
                  {isReadOnly ? `Riepilogo Controllo · Target ${targetPct}%` : `Target ${targetPct}% · Controllo Camere`}
                </div>
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={toggleRecapPin}
                    data-testid="recap-pin-btn"
                    title={recapPinned ? "Rimuovi fissaggio" : "Fissa in alto durante lo scroll"}
                    className={`-mt-1 -mr-1 h-7 w-7 rounded-full flex items-center justify-center transition-all active:scale-[0.9] ${
                      recapPinned
                        ? "bg-lavender-500 text-white shadow-sm shadow-lavender-500/30"
                        : "text-lavender-700 dark:text-lavender-300 hover:bg-lavender-100 dark:hover:bg-lavender-900/40"
                    }`}
                  >
                    <Pin className={`h-3.5 w-3.5 ${recapPinned ? "" : "rotate-45"}`} fill={recapPinned ? "currentColor" : "none"} />
                  </button>
                )}
              </div>
              {isReadOnly && !isLegacyCompletato ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div><div className="text-[10px] uppercase text-muted-foreground">Totale hotel</div><div className="font-display text-2xl tabular-nums" data-testid="metric-total">{totale}</div></div>
                    <div><div className="text-[10px] uppercase text-muted-foreground">Compilate</div><div className="font-display text-2xl tabular-nums" data-testid="metric-compiled">{compiledRooms.length}</div></div>
                    <div><div className="text-[10px] uppercase text-muted-foreground">Conformi</div><div className="font-display text-2xl tabular-nums text-emerald-600 dark:text-emerald-400" data-testid="metric-conformi">{conformiCount}</div><div className="text-[10px] text-emerald-600 dark:text-emerald-400 tabular-nums" data-testid="metric-conformi-pct">{conformiPct}%</div></div>
                    <div><div className="text-[10px] uppercase text-muted-foreground">Non conformi</div><div className={`font-display text-2xl tabular-nums ${nonConformiCount > 0 ? "text-rose-600" : "text-muted-foreground"}`} data-testid="metric-non-conformi">{nonConformiCount}</div><div className={`text-[10px] tabular-nums ${nonConformiCount > 0 ? "text-rose-600" : "text-muted-foreground"}`} data-testid="metric-non-conformi-pct">{nonConformiPct}%</div></div>
                    <div><div className="text-[10px] uppercase text-muted-foreground">Con criticità</div><div className={`font-display text-2xl tabular-nums ${criticheCount > 0 ? "text-amber-600" : "text-muted-foreground"}`} data-testid="metric-critiche">{criticheCount}</div><div className={`text-[10px] tabular-nums ${criticheCount > 0 ? "text-amber-600" : "text-muted-foreground"}`} data-testid="metric-critiche-pct">{criticitaPct}%</div></div>
                  </div>
                  {/* Barra segmentata v99+ */}
                  <div className="mt-3 h-2 rounded-full bg-black/10 dark:bg-white/10 overflow-hidden flex" data-testid="metric-segmented-bar">
                    <div className="h-full bg-emerald-500" style={{ width: `${conformiPct}%` }} />
                    <div className="h-full bg-amber-500" style={{ width: `${criticitaPct}%` }} />
                    <div className="h-full bg-rose-500" style={{ width: `${nonConformiPct}%` }} />
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
                    <span className="text-emerald-600 dark:text-emerald-400"><span className="tabular-nums">{conformiPct}%</span> conformi</span>
                    <span className="text-amber-600 dark:text-amber-400"><span className="tabular-nums">{criticitaPct}%</span> con criticità</span>
                    <span className="text-rose-600 dark:text-rose-400"><span className="tabular-nums">{nonConformiPct}%</span> non conformi</span>
                    <span className="text-muted-foreground/70">· su {compiledRooms.length} camere controllate</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div><div className="text-[10px] uppercase text-muted-foreground">Totale hotel</div><div className="font-display text-2xl tabular-nums" data-testid="metric-total">{totale}</div></div>
                    <div><div className="text-[10px] uppercase text-muted-foreground">Minimo {targetPct}%</div><div className="font-display text-2xl tabular-nums" data-testid="metric-min">{minimo}</div></div>
                    <div><div className="text-[10px] uppercase text-muted-foreground">Compilate</div><div className={`font-display text-2xl tabular-nums ${belowThreshold ? "text-rose-600" : "text-emerald-600"}`} data-testid="metric-compilate">{compilate}</div></div>
                    <div><div className="text-[10px] uppercase text-muted-foreground">Mancanti</div><div className={`font-display text-2xl tabular-nums ${mancantiAlMinimo > 0 ? "text-rose-600" : "text-muted-foreground"}`} data-testid="metric-missing">{mancantiAlMinimo}</div></div>
                    {/* v102 — Delta vs Minimo: +N verde / -N rosso / ±0 grigio */}
                    {(() => {
                      const delta = compilate - minimo;
                      const sign = delta > 0 ? "+" : delta < 0 ? "" : "±";
                      const deltaCls = delta > 0 ? "text-emerald-600 dark:text-emerald-400" : delta < 0 ? "text-rose-600 dark:text-rose-400" : "text-muted-foreground";
                      return (
                        <div>
                          <div className="text-[10px] uppercase text-muted-foreground">Delta minimo</div>
                          <div className={`font-display text-2xl tabular-nums font-semibold ${deltaCls}`} data-testid="metric-delta">
                            {sign}{delta}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-black/10 dark:bg-white/10 overflow-hidden">
                    <div className={`h-full transition-all ${barCls}`} style={{ width: `${pct}%` }} data-testid="metric-bar" />
                  </div>
                  {inSospeso > 0 && (
                    <div className="mt-2 text-[11px] text-muted-foreground" data-testid="metric-in-sospeso">
                      In sospeso: <span className="tabular-nums text-foreground/80">{inSospeso}</span> {inSospeso === 1 ? "camera aperta" : "camere aperte"} non ancora compilate
                    </div>
                  )}
                  {belowThreshold && <div className="mt-2 text-xs text-rose-600 dark:text-rose-400">Compila almeno {minimo} camere per raggiungere il minimo del {targetPct}%</div>}
                  {sup > 0 && <div className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">+{sup} camere sopra soglia — copertura extra</div>}
                </>
              )}
            </div>

            {isLegacyCompletato && (
              <div className="rounded-xl border border-amber-200/60 dark:border-amber-900/40 bg-amber-50/40 dark:bg-amber-950/20 p-3 text-xs text-amber-800 dark:text-amber-200">
                <strong>Formato legacy:</strong> questo controllo è stato completato con la vecchia checklist. Sola lettura.
              </div>
            )}

            {/* Filters + v101 bulk actions */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input type="text" placeholder="Cerca camera…" value={roomSearch} onChange={(e) => setRoomSearch(e.target.value)} className="w-full pl-9 pr-3 h-9 rounded-lg border border-border bg-background text-sm" data-testid="rooms-search-input" />
              </div>
              <div className="flex items-center gap-1 overflow-x-auto">
                {FILTERS.map((f) => (
                  <button key={f.k} type="button" onClick={() => setSelectFilter(f.k)} data-testid={`filter-${f.k}`} className={`px-3 h-8 rounded-full text-xs whitespace-nowrap transition-colors ${selectFilter === f.k ? "bg-lavender-500 text-white" : "border border-border hover:bg-accent"}`}>{f.l}</button>
                ))}
              </div>
            </div>

            {/* v101 — Bulk actions: Suggerisci 20% + Deseleziona tutto (solo in edit) */}
            {!isReadOnly && (
              <div className="flex flex-wrap items-center gap-2 -mt-1">
                <button
                  type="button"
                  onClick={handleSuggest20}
                  disabled={suggesting}
                  data-testid="suggest-20-btn"
                  title={`Seleziona ${minimo} camere smart non controllate negli ultimi 7 giorni`}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold bg-lavender-500 text-white hover:bg-lavender-600 disabled:opacity-50 transition-all active:scale-[0.97] shadow-sm shadow-lavender-500/20"
                >
                  {suggesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  Suggerisci {targetPct}%
                  <span className="ml-0.5 text-[10px] opacity-80 tabular-nums">· {minimo}</span>
                </button>
                <button
                  type="button"
                  onClick={handleDeselectAll}
                  disabled={sel === 0}
                  data-testid="deselect-all-btn"
                  title="Deseleziona tutte le camere selezionate (non compilate)"
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold border border-border bg-background hover:bg-accent disabled:opacity-50 transition-all active:scale-[0.97]"
                >
                  <Ban className="h-3.5 w-3.5" />
                  Deseleziona tutto
                  {sel > 0 && <span className="ml-0.5 text-[10px] opacity-70 tabular-nums">· {sel}</span>}
                </button>
                {sel === 0 && (
                  <span className="text-[11px] text-muted-foreground italic">
                    Nessuna camera selezionata. Usa "Suggerisci {targetPct}%" per la rotazione smart, oppure scegli manualmente dalla griglia.
                  </span>
                )}
              </div>
            )}

            {/* Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="rooms-grid">
              {filtered.map((r) => {
                const isSel = r.is_selected;
                const compiled = r.compilata;
                const bgStyle = isSel && !compiled ? { backgroundColor: "#e7d7f6" } : undefined;
                const hasCriticita = compiled && (r.record?.voci || []).some((v) => v.stato === "non_ok");
                const nonConforme = compiled && ["sufficiente", "insufficiente"].includes(r.record?.valutazione);
                const cardCls = compiled
                  ? (nonConforme
                      ? "bg-rose-50/60 dark:bg-rose-950/30 border-rose-300 dark:border-rose-800 text-rose-900 dark:text-rose-100"
                      : hasCriticita
                        ? "bg-amber-50/60 dark:bg-amber-950/30 border-amber-300 dark:border-amber-800 text-amber-900 dark:text-amber-100"
                        : "bg-emerald-50/60 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-800 text-emerald-900 dark:text-emerald-100")
                  : isSel ? "border-lavender-400 text-lavender-900 dark:text-lavender-100"
                  : "bg-background border-border hover:border-lavender-300 text-foreground";
                const badgeEl = r.badge === "ieri" ? <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-rose-500 text-white" title="Controllata ieri">IERI</span>
                  : r.badge === "settimana" ? <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500 text-white" title="Già questa settimana">SETT</span> : null;
                // In read-only le camere compilate sono cliccabili → dettaglio read-only.
                const clickable = compiled || (isSel && !isReadOnly);
                return (
                  <div key={r.room_id} className="relative">
                    <button type="button" data-testid={`room-card-${r.room_id}`} onClick={() => { if (clickable) navigate(`/controlli/${id}/camere/${r.room_id}`); }}
                      disabled={!clickable} className={`w-full px-3 py-2.5 rounded-xl border text-left transition-all ${cardCls} ${!clickable ? "cursor-default" : "active:scale-[0.97] hover:shadow-sm cursor-pointer"}`}
                      style={{ ...(bgStyle || {}), minHeight: 76 }}>
                      <div className="flex items-start justify-between gap-1">
                        <div className="font-medium text-sm">{r.numero}</div>
                        <div className="flex items-center gap-1">
                          {badgeEl}
                          {compiled && (r.record?.foto_ids?.length ?? 0) > 0 && (
                            <span className="text-[9px] font-bold px-1 py-0.5 rounded-full bg-lavender-500 text-white flex items-center gap-0.5" title={`${r.record.foto_ids.length} foto`}>
                              <ImageIcon className="h-2.5 w-2.5" />{r.record.foto_ids.length}
                            </span>
                          )}
                          {compiled && !nonConforme && <Check className="h-3.5 w-3.5 text-emerald-600" />}
                        </div>
                      </div>
                      <div className={`text-[10px] mt-0.5 truncate ${compiled ? (nonConforme ? "text-rose-700 dark:text-rose-300" : hasCriticita ? "text-amber-700 dark:text-amber-300" : "text-emerald-700 dark:text-emerald-300") : isSel ? "text-lavender-800 dark:text-lavender-200" : "text-muted-foreground"}`}>
                        {compiled && r.record ? <span className="inline-flex items-center gap-1"><span className={`h-1.5 w-1.5 rounded-full ${VOTI[r.record.valutazione]?.cls || "bg-slate-400"}`} />{VOTI[r.record.valutazione]?.l || r.record.valutazione}{hasCriticita ? ` · ${(r.record.voci || []).filter((v) => v.stato === "non_ok").length} criticità` : ""}</span>
                          : isSel ? "Da compilare" : (r.tipologia || (r.piano ? `Piano ${r.piano}` : "—"))}
                      </div>
                      {compiled && r.record?.nota_camera && (
                        <div className="text-[10px] mt-1 italic text-muted-foreground line-clamp-2" title={r.record.nota_camera}>
                          "{r.record.nota_camera}"
                        </div>
                      )}
                    </button>
                    {!isReadOnly && !compiled && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); if (isSel) setDeselectMenu(r); else toggleSelect(r, true); }} data-testid={`toggle-${r.room_id}`}
                        className={`absolute top-1.5 right-1.5 h-6 w-6 rounded-full flex items-center justify-center border transition-colors ${isSel ? "bg-white/80 text-lavender-700 hover:bg-rose-100 hover:text-rose-600 border-lavender-200" : "bg-white/80 text-emerald-600 hover:bg-emerald-100 border-emerald-200"}`}
                        title={isSel ? "Deseleziona" : "Aggiungi al controllo"}>
                        {isSel ? <X className="h-3.5 w-3.5" /> : <PlusIcon className="h-3.5 w-3.5" />}
                      </button>
                    )}
                  </div>
                );
              })}
              {filtered.length === 0 && <div className="col-span-full text-center text-xs text-muted-foreground py-6">Nessuna camera per il filtro selezionato</div>}
            </div>
          </div>
        );
      })()}

      <div className={`ih-glass rounded-2xl p-6 ${isCamere ? "hidden" : ""}`}>
        {/* Fix 1: Card punteggio conformità in read-only per task NON camere */}
        {isReadOnly && (() => {
          const punteggio = check?.punteggio_totale;
          const results = check?.checklist_results || [];
          const total = results.length;
          const conformi = results.filter((r) => r.valore === true).length;
          const critiche = results.filter((r) => r.valore === false).length;
          const naCount = results.filter((r) => r.valore === "na").length;
          const scoreColor = punteggio == null
            ? "text-muted-foreground"
            : punteggio >= 85
              ? "text-emerald-600 dark:text-emerald-400"
              : punteggio >= 65
                ? "text-amber-600 dark:text-amber-400"
                : "text-rose-600 dark:text-rose-400";
          const scoreBg = punteggio == null
            ? "bg-muted/40 border-border/50"
            : punteggio >= 85
              ? "bg-emerald-50/60 dark:bg-emerald-950/30 border-emerald-300/60 dark:border-emerald-800/50"
              : punteggio >= 65
                ? "bg-amber-50/60 dark:bg-amber-950/30 border-amber-300/60 dark:border-amber-800/50"
                : "bg-rose-50/60 dark:bg-rose-950/30 border-rose-300/60 dark:border-rose-800/50";
          return (
            <div
              data-testid="readonly-score-card"
              className={`mb-5 rounded-xl border px-5 py-4 flex items-center justify-between gap-4 flex-wrap ${scoreBg}`}
            >
              <div className="flex-1 min-w-[180px]">
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-0.5">Punteggio conformità</div>
                <div className="text-sm text-muted-foreground">
                  {punteggio != null ? (
                    <>
                      <span className="tabular-nums font-medium text-foreground">{conformi}</span> su{" "}
                      <span className="tabular-nums font-medium text-foreground">{total}</span> voci conformi
                      {critiche > 0 && <> · <span className="text-rose-600 dark:text-rose-400 font-medium">{critiche} criticità</span></>}
                      {naCount > 0 && <> · <span className="text-muted-foreground">{naCount} N.A.</span></>}
                    </>
                  ) : (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center gap-1 cursor-help underline decoration-dotted underline-offset-2">
                            Punteggio non disponibile
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>Punteggio non calcolato (task precedente all'aggiornamento)</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </div>
              <div className="flex items-baseline gap-1">
                <span
                  data-testid="readonly-score-value"
                  className={`font-display text-4xl tabular-nums font-medium ${scoreColor}`}
                >
                  {punteggio ?? "—"}
                </span>
                <span className="text-sm text-muted-foreground">/100</span>
              </div>
            </div>
          );
        })()}

        <h2 className="font-display text-xl tracking-tight mb-4">Checklist</h2>
        <div className="space-y-3">
          {checklistItems.map((it) => {
            const v = results[it.id]?.valore;
            return (
              <div key={it.id} className="rounded-xl border border-border/60 p-4 bg-background/40">
                <div className="flex items-start gap-3">
                  <ChecklistStatusIcon
                    value={v}
                    size="md"
                    className="mt-0.5"
                    testId={`item-yes-${it.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm break-words">{it.label}</span>
                      {it.obbligatorio && <span className="shrink-0 px-1.5 py-0.5 rounded text-[9px] bg-lavender-200/60 text-lavender-900 dark:bg-lavender-900/40 dark:text-lavender-200 uppercase tracking-wider">Obbligatorio</span>}
                      {it.richiede_foto && <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] bg-lavender-100 text-lavender-800 dark:bg-lavender-900/40 dark:text-lavender-300 uppercase tracking-wider"><Camera className="w-3.5 h-3.5 shrink-0" /> Foto consigliata</span>}
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <button
                        disabled={isReadOnly}
                        onClick={() => setResults((r) => ({ ...r, [it.id]: { ...r[it.id], valore: true } }))}
                        className={`text-xs px-2 py-1 rounded-md border ${v === true ? "bg-emerald-500 text-white border-emerald-500" : "bg-background/60 border-border/60 text-muted-foreground hover:bg-emerald-50 dark:hover:bg-emerald-900/30"}`}
                        data-testid={`item-yes-btn-${it.id}`}
                      >Conforme</button>
                      <button
                        disabled={isReadOnly}
                        onClick={() => setResults((r) => ({ ...r, [it.id]: { ...r[it.id], valore: false } }))}
                        className={`text-xs px-2 py-1 rounded-md border ${v === false ? "bg-rose-500 text-white border-rose-500" : "bg-background/60 border-border/60 text-muted-foreground hover:bg-rose-50 dark:hover:bg-rose-900/30"}`}
                        data-testid={`item-no-${it.id}`}
                      >Non conforme</button>
                      <button
                        disabled={isReadOnly}
                        onClick={() => setResults((r) => ({ ...r, [it.id]: { ...r[it.id], valore: "na" } }))}
                        className={`text-xs px-2 py-1 rounded-md border ${v === "na" ? "bg-slate-400 text-white border-slate-400" : "bg-background/60 border-border/60 text-muted-foreground hover:bg-slate-100 dark:hover:bg-slate-800"}`}
                        data-testid={`item-na-${it.id}`}
                      >N/A</button>
                    </div>
                    <Textarea
                      disabled={isReadOnly}
                      placeholder="Note (opzionale)"
                      value={results[it.id]?.note || ""}
                      onChange={(e) => setResults((r) => ({ ...r, [it.id]: { ...r[it.id], note: e.target.value } }))}
                      className="mt-2 text-xs min-h-[40px] bg-background/40"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Photos */}
      <div className="ih-glass rounded-2xl p-6">
        <h2 className="font-display text-xl tracking-tight mb-1">
          Foto
          {check?.categoria === "locali_tecnici" && (
            <span className="ml-2 text-xs font-normal text-muted-foreground tabular-nums" data-testid="photo-limit-badge">
              {fotos.length}/10
            </span>
          )}
        </h2>
        <p className="text-xs text-muted-foreground mb-4">
          {missing.requiresFoto ? "Almeno 1 foto richiesta per completare." : "Foto opzionali."}
          {check?.categoria === "locali_tecnici" && " · Max 10 foto per Locali Tecnici."}
        </p>
        <div className="flex flex-wrap gap-3">
          {fotos.map((f) => (
            <div key={f.id} className="relative w-24 h-24 rounded-xl overflow-hidden border border-border/60 group">
              <img src={f.url} alt="" className="w-full h-full object-cover" />
              {!isReadOnly && (
                <button onClick={() => removeFoto(f.id)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
          {!isReadOnly && !(check?.categoria === "locali_tecnici" && fotos.length >= 10) && (
            <button
              data-testid="upload-photo-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-24 h-24 rounded-xl border-2 border-dashed border-border/60 flex flex-col items-center justify-center gap-1 text-muted-foreground hover:bg-accent/40 transition-colors"
            >
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImageIcon className="h-5 w-5" />}
              <span className="text-[10px]">Aggiungi</span>
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFileChange} />
        </div>
        {!isReadOnly && (
          <p className="text-[11px] text-muted-foreground mt-3" data-testid="retention-hint">
            Le foto vengono compresse automaticamente e conservate per <strong>30 giorni</strong>.
          </p>
        )}
      </div>

      {/* Commenti */}
      <div className={`ih-glass rounded-2xl p-6 ${isCamere ? "hidden" : ""}`}>
        <h2 className="font-display text-xl tracking-tight mb-3">Commenti generali</h2>
        <Textarea data-testid="commenti-input" disabled={isReadOnly} value={commenti} onChange={(e) => setCommenti(e.target.value)} placeholder="Aggiungi un commento finale (opzionale)" className="bg-background/40 min-h-[100px]" />
      </div>

      {/* Valutazione (nascosta per camere: deriva da media camere) */}
      <div className={`ih-glass rounded-2xl p-6 ${isCamere ? "hidden" : ""}`}>
        <h2 className="font-display text-xl tracking-tight mb-3">Valutazione complessiva</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {VAL_BUTTONS.map((b) => (
            <button
              key={b.v}
              data-testid={`valutazione-${b.v}`}
              disabled={isReadOnly}
              onClick={() => {
                if (b.v === "na") {
                  // v101 — Apri modal nota obbligatoria
                  setNaNote(commenti || "");
                  setNaModalOpen(true);
                } else {
                  setValutazione(b.v);
                }
              }}
              className={`rounded-xl border-2 p-4 font-display text-sm font-semibold transition-all ${valutazione === b.v ? b.color + " shadow-lg" : "bg-background/40 border-border/60 hover:border-foreground/30 text-foreground"}`}
            >
              {b.label}
            </button>
          ))}
        </div>
        {valutazione === "na" && commenti && (
          <div className="mt-3 rounded-lg border border-slate-300/60 bg-slate-50/40 dark:bg-slate-900/30 dark:border-slate-700/60 p-3 text-xs" data-testid="na-nota-preview">
            <span className="text-slate-500 dark:text-slate-400 uppercase tracking-wider text-[10px] font-semibold">Motivo N/A:</span>
            <span className="ml-2 italic">{commenti}</span>
          </div>
        )}
      </div>

      {/* Firma (se completato) */}
      {check.firma && (
        <div className="ih-glass rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-2"><PenLine className="h-4 w-4 text-lavender-500" /> <span className="font-display text-lg">Firma digitale</span></div>
          <div className="text-sm">Firmato da <span className="font-semibold">{check.firma.nome_cognome}</span></div>
          <div className="text-xs text-muted-foreground">il {new Date(check.firma.timestamp).toLocaleString("it-IT")}</div>
        </div>
      )}

      {/* Action */}
      {!isReadOnly && (() => {
        const c = camereData?.counters || {};
        const minimo = c.minimo ?? c.minimo_richiesto ?? 0;
        const compilate = c.compilate ?? 0;
        const inSospeso = c.in_sospeso ?? 0;
        const mancantiAlMinimo = c.mancanti_al_minimo ?? Math.max(0, minimo - compilate);
        return (
        <div className="sticky bottom-4 ih-glass-strong rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-muted-foreground" data-testid="complete-status-msg">
            {!canComplete && (
              <span>
                {isCamere
                  ? (minimo === 0
                      ? "Nessuna camera nell'hotel"
                      : compilate === 0
                        ? `Compila almeno ${minimo} camer${minimo === 1 ? "a" : "e"} per completare`
                        : `Mancano ${mancantiAlMinimo} camer${mancantiAlMinimo === 1 ? "a" : "e"} al minimo di ${minimo}`)
                  : (<>
                      {!valutazione && "Seleziona valutazione · "}
                      {missing.items.length > 0 && `Mancano risposte: ${missing.items.slice(0, 2).join(", ")}${missing.items.length > 2 ? "…" : ""}`}
                    </>)}
              </span>
            )}
            {canComplete && (
              <span className={inSospeso > 0 ? "text-amber-600 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-400"}>
                {isCamere
                  ? (inSospeso > 0
                      ? `Minimo raggiunto (${compilate}/${minimo}). ${inSospeso} camer${inSospeso === 1 ? "a ancora aperta" : "e ancora aperte"} — puoi chiuderle o completare comunque.`
                      : `Tutte le camere selezionate sono state compilate (${compilate}/${minimo}). Puoi completare il controllo.`)
                  : "Pronto per il completamento"}
              </span>
            )}
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* v113 — "Completa comunque" sotto soglia (solo camere, con compilate>0) */}
                  {isCamere && !canComplete && compilate > 0 && compilate < minimo && (
                    <Button
                      data-testid="force-complete-btn"
                      variant="outline"
                      onClick={() => { setForceReason(""); setForceOpen(true); }}
                      className="gap-2 border-amber-400 text-amber-700 hover:bg-amber-50 dark:border-amber-500/60 dark:text-amber-300 dark:hover:bg-amber-950/30"
                    >
                      <AlertTriangle className="h-4 w-4" /> Completa comunque
                    </Button>
                  )}
                  <Button
                    data-testid="complete-btn"
                    disabled={!canComplete}
                    onClick={() => {
                      if (isCamere && inSospeso > 0) {
                        setInSospesoConfirmOpen(true);
                      } else {
                        setConfirmOpen(true);
                      }
                    }}
                    className="gap-2"
                  >
                    <CheckCircle2 className="h-4 w-4" /> Completa controllo
                  </Button>
                </div>
              </TooltipTrigger>
              {!canComplete && (
                <TooltipContent>
                  {isCamere
                    ? `Compila almeno ${minimo} camer${minimo === 1 ? "a" : "e"} per raggiungere il minimo`
                    : (!valutazione ? "Seleziona una valutazione" : missing.foto ? "Carica almeno una foto" : "Rispondi a tutti gli item obbligatori")}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
        );
      })()}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="ih-glass-strong">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Confermi il completamento?</DialogTitle>
            <DialogDescription>Stai firmando digitalmente questo controllo. L'azione è irreversibile.</DialogDescription>
          </DialogHeader>
          <div className="rounded-xl bg-accent/40 p-4 text-sm">
            <div className="flex items-center gap-2 mb-1"><PenLine className="h-4 w-4 text-lavender-500" /> Firmato da <span className="font-semibold">{user?.nome} {user?.cognome}</span></div>
            <div className="text-xs text-muted-foreground">il {new Date().toLocaleString("it-IT")}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Annulla</Button>
            <Button data-testid="confirm-complete" onClick={() => submit()} disabled={submitting}>{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Firma e completa"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v113 — Dialog "Completa comunque" sotto soglia camere */}
      <Dialog open={forceOpen} onOpenChange={(o) => { if (!o) setForceOpen(false); }}>
        <DialogContent className="ih-glass-strong" data-testid="force-complete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Chiusura sotto soglia
            </DialogTitle>
            <DialogDescription>
              Hai compilato <b>{camereData?.counters?.compilate ?? 0}</b> camere su <b>{camereData?.counters?.minimo ?? camereData?.counters?.minimo_richiesto ?? 0}</b> previste
              (soglia minima {camereData?.counters?.target_pct ?? 20}%).
              Specifica la motivazione (minimo 20 caratteri) per chiudere comunque il controllo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Textarea
              data-testid="force-complete-reason"
              value={forceReason}
              onChange={(e) => setForceReason(e.target.value)}
              placeholder="Es: Hotel in bassa stagione con 3 camere occupate, impossibile raggiungere la soglia minima prevista…"
              rows={5}
              maxLength={500}
              className="resize-none"
            />
            <div className="text-[11px] text-muted-foreground flex justify-between">
              <span>{forceReason.length < 20 ? `Mancano ${20 - forceReason.length} caratteri` : "Motivazione sufficiente"}</span>
              <span>{forceReason.length}/500</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForceOpen(false)}>Annulla</Button>
            <Button
              data-testid="confirm-force-complete"
              onClick={() => submit(true, forceReason.trim())}
              disabled={submitting || forceReason.trim().length < 20}
              className="bg-amber-500 hover:bg-amber-600 text-white gap-2"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />}
              Conferma chiusura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v101 — Modal N/A: nota obbligatoria quando valutazione = "na" */}
      <Dialog open={naModalOpen} onOpenChange={(o) => { if (!o) setNaModalOpen(false); }}>
        <DialogContent className="ih-glass-strong" data-testid="na-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <Ban className="h-5 w-5 text-slate-500" />
              Perché N/A?
            </DialogTitle>
            <DialogDescription>
              Marcando questa task come <strong>Non Applicabile</strong> non entrerà nei KPI. Indica il motivo (obbligatorio).
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={naNote}
            onChange={(e) => setNaNote(e.target.value)}
            placeholder={`Es. "Non abbiamo il ${(check?.titolo || "servizio").toLowerCase()} nella struttura"`}
            className="min-h-[100px] bg-background/40"
            autoFocus
            data-testid="na-note-input"
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setNaModalOpen(false)} data-testid="na-cancel">
              Annulla
            </Button>
            <Button
              onClick={() => {
                const n = naNote.trim();
                if (!n) {
                  toast.error("Il motivo è obbligatorio per N/A");
                  return;
                }
                setValutazione("na");
                setCommenti(n);
                setNaModalOpen(false);
                toast.success("Valutazione impostata su N/A");
              }}
              className="bg-slate-500 hover:bg-slate-600 text-white gap-1.5"
              data-testid="na-confirm"
            >
              <Ban className="h-4 w-4" /> Conferma N/A
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conferma per camere in sospeso (v98+) */}
      <Dialog open={inSospesoConfirmOpen} onOpenChange={setInSospesoConfirmOpen}>
        <DialogContent className="ih-glass-strong sm:max-w-md" data-testid="in-sospeso-confirm-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Camere ancora aperte
            </DialogTitle>
            <DialogDescription>
              Hai <strong className="text-foreground">{camereData?.counters?.in_sospeso ?? 0}</strong> camer{(camereData?.counters?.in_sospeso ?? 0) === 1 ? "a ancora aperta" : "e ancora aperte"} che non hanno esito. Verrann{(camereData?.counters?.in_sospeso ?? 0) === 1 ? "o chiuse" : "o chiuse"} come <strong>non controllate</strong> e non impatteranno il punteggio.
              <br /><br />
              Il minimo del {camereData?.counters?.target_pct ?? 20}% ({camereData?.counters?.minimo ?? 0} camere) è comunque raggiunto con le <strong>{camereData?.counters?.compilate ?? 0}</strong> camere compilate.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInSospesoConfirmOpen(false)}>Annulla</Button>
            <Button
              data-testid="confirm-in-sospeso"
              onClick={() => { setInSospesoConfirmOpen(false); setConfirmOpen(true); }}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Completa comunque
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!skipTarget} onOpenChange={(o) => { if (!o) setSkipTarget(null); }}>
        <DialogContent className="ih-glass-strong sm:max-w-md" data-testid="skip-room-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <Ban className="h-4 w-4 text-lavender-600" />
              Camera non controllabile
            </DialogTitle>
            <DialogDescription>
              Camera <span className="font-semibold text-foreground">{skipTarget?.numero}</span> · il sistema ti suggerirà automaticamente una camera sostitutiva rispettando la rotazione intelligente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Motivo</label>
              <Select value={skipMotivo} onValueChange={setSkipMotivo}>
                <SelectTrigger data-testid="skip-motivo-trigger" className="bg-background/40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DND / Non disturbare">DND / Non disturbare</SelectItem>
                  <SelectItem value="Cliente dentro">Cliente dentro</SelectItem>
                  <SelectItem value="Manutenzione">Manutenzione</SelectItem>
                  <SelectItem value="Altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                Note {skipMotivo === "Altro" ? <span className="text-rose-500 normal-case tracking-normal">(obbligatorie)</span> : <span className="text-muted-foreground/70 normal-case tracking-normal">(opzionali)</span>}
              </label>
              <Textarea
                data-testid="skip-note-input"
                value={skipNote}
                onChange={(e) => setSkipNote(e.target.value)}
                placeholder={skipMotivo === "Altro" ? "Specifica il motivo…" : "Aggiungi una nota (opzionale)…"}
                className="bg-background/40 min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSkipTarget(null)}>Annulla</Button>
            <Button data-testid="skip-confirm" onClick={submitSkipRoom} className="gap-1.5">
              <Ban className="h-4 w-4" /> Conferma e sostituisci
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!deselectMenu} onOpenChange={(o) => { if (!o) setDeselectMenu(null); }}>
        <DialogContent className="sm:max-w-sm" data-testid="deselect-menu">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2"><X className="h-4 w-4 text-rose-500" /> Deseleziona camera {deselectMenu?.numero}</DialogTitle>
            <DialogDescription>Motivo opzionale (statistico). La camera esce dalla lista di oggi ma può essere riaggiunta.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-2 py-1">
            {[null, "DND", "Cliente dentro", "Manutenzione", "Altro"].map((m) => (
              <button key={m || "none"} type="button" data-testid={`deselect-reason-${m || "none"}`}
                onClick={() => toggleSelect(deselectMenu, false, m || undefined)}
                className="text-left px-3 py-2 rounded-lg border border-border hover:bg-lavender-50 dark:hover:bg-lavender-950/30 text-sm">
                {m || "— (nessun motivo)"}
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeselectMenu(null)}>Annulla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={belowThresholdOpen} onOpenChange={setBelowThresholdOpen}>
        <DialogContent className="sm:max-w-md" data-testid="below-threshold-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2 text-amber-600"><Sparkles className="h-5 w-5" /> Sotto la soglia minima</DialogTitle>
            <DialogDescription>
              Hai selezionato <strong className="text-foreground tabular-nums">{camereData?.counters?.selezionate ?? 0}</strong> camere su <strong className="text-foreground tabular-nums">{camereData?.counters?.minimo_richiesto ?? 0}</strong> minime richieste ({camereData?.counters?.target_pct ?? 20}% del totale). Puoi completare comunque, ma la task verrà segnalata come sotto soglia nei KPI.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBelowThresholdOpen(false)}>Annulla</Button>
            <Button onClick={() => { setBelowThresholdOpen(false); submit(); }} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="below-threshold-continue">
              Completa comunque
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
